import React from 'react'
import StarMarker from './StarMarker'
import { Column, Flex, ProgressBar, Row, Text } from '@/components/core'
import { Match, Switch } from '@/components/switch'
import { classNameBuilder } from '@/helpers'

const ENGLISH_LEVELS = [
  'A0',
  'A1',
  'A1+',
  'A2',
  'A2+',
  'B1',
  'B1+',
  'B2',
  'B2+',
  'C1',
  'C1+',
  'C2'
] as const

export type EnglishLevelsType = (typeof ENGLISH_LEVELS)[number]

const FLUENT_LEVEL_START_INDEX = 7

export interface EnglishLevelBarProps {
  level: EnglishLevelsType
  isResponsive?: boolean
}
export const EnglishLevelBar: React.FC<EnglishLevelBarProps> = ({
  level,
  isResponsive = true
}) => {
  const levelIndex = ENGLISH_LEVELS.findIndex((item) => item === level)
  const isFluent = levelIndex > FLUENT_LEVEL_START_INDEX
  return (
    <Column>
      <Row className="z-10 ml-[-12px] items-end justify-between">
        {ENGLISH_LEVELS.map((level, index) => {
          const isFirstItem = !index
          const isLastItem = index === ENGLISH_LEVELS.length - 1
          const isFluentMarker = index === FLUENT_LEVEL_START_INDEX
          const isCurrentLevel = index === levelIndex
          const higherThanCurrentLevel = levelIndex < index

          return (
            <Column
              key={index}
              className={classNameBuilder(
                'w-12 items-center space-y-3',
                isResponsive && 'max-lg:w-7 max-lg:space-y-0'
              )}
            >
              <Text
                color="none"
                fontWeight="bold"
                className={classNameBuilder(
                  'text-[12px]',
                  isResponsive && 'max-lg:text-[10px]',
                  isCurrentLevel
                    ? 'text-electric-indigo-500'
                    : higherThanCurrentLevel
                      ? 'text-gray-100'
                      : 'text-electric-indigo-200'
                )}
              >
                {level}
              </Text>
              <Flex className="h-5 items-center">
                <Switch>
                  <Match when={isFluentMarker}>
                    <StarMarker active={isFluent} />
                  </Match>
                  <Match when={!isFluentMarker && !isFirstItem && !isLastItem}>
                    <div
                      className={classNameBuilder(
                        'h-3 w-[2px] rounded-full',
                        isCurrentLevel
                          ? 'bg-electric-indigo-500'
                          : higherThanCurrentLevel
                            ? 'bg-gray-100'
                            : 'bg-electric-indigo-200'
                      )}
                    />
                  </Match>
                </Switch>
              </Flex>
            </Column>
          )
        })}
      </Row>
      <Row
        className={classNameBuilder('mx-1 px-3', isResponsive && 'max-lg:mx-0')}
      >
        <ProgressBar
          progress={(1 / (ENGLISH_LEVELS.length - 1)) * levelIndex}
          className={{
            wrapper: '-mt-3 h-1 w-full',
            divider: false
          }}
          multiBars={[
            {
              barClassName: 'bg-gradient-to-r from-[#09E1D5] to-[#6A0DFD]',
              bgClassName: 'bg-[#E0E0E0]',
              weight: 7
            }
          ]}
        />
      </Row>
    </Column>
  )
}

export default EnglishLevelBar
